import dayjs from "dayjs"
import { isServerGeneratedId, AVAILABILITY_TYPES } from "./constants"

export const appendProfileFields = (formData, formDataPayload, userId) => {
  formDataPayload.append("profile[status]", "active")

  if (userId) {
    formDataPayload.append("profile[user_id]", userId)
  }
  if (formData.permissionRole) {
    formDataPayload.append("profile[role_id]", formData.permissionRole)
  }
  if (formData.firstName) {
    formDataPayload.append("profile[first_name]", formData.firstName)
  }
  if (formData.lastName) {
    formDataPayload.append("profile[last_name]", formData.lastName)
  }
  if (formData.email) {
    formDataPayload.append("profile[email]", formData.email)
  }
  if (formData.phone) {
    formDataPayload.append("profile[phone_number]", formData.phone)
  }
  if (formData.phoneCountryCode) {
    formDataPayload.append("profile[country_code]", formData.phoneCountryCode)
  }
  if (formData.title) {
    formDataPayload.append("profile[title]", formData.title)
  }
  if (formData.description) {
    formDataPayload.append("profile[description]", formData.description)
  }

  formDataPayload.append("profile[all_year]", formData.availability === AVAILABILITY_TYPES.ALL_YEAR)

  if (formData.avatar instanceof File) {
    formDataPayload.append("profile[photo]", formData.avatar)
  }
}

const appendLicense = (license, index, formDataPayload) => {
  const prefix = `profile[staff_documents_attributes][${index}]`

  if (license.id) {
    formDataPayload.append(`${prefix}[id]`, license.id)

    if (license._destroy) {
      formDataPayload.append(`${prefix}[_destroy]`, "true")
      return
    }

    if (license.name) formDataPayload.append(`${prefix}[title]`, license.name)
    if (license.description) formDataPayload.append(`${prefix}[notes]`, license.description)
    if (license.issueDate) {
      formDataPayload.append(`${prefix}[issue_date]`, dayjs(license.issueDate).format("YYYY-MM-DD"))
    }
    if (license.expiryDate) {
      formDataPayload.append(`${prefix}[expiry_date]`, dayjs(license.expiryDate).format("YYYY-MM-DD"))
    }
    if (license.file instanceof File) {
      formDataPayload.append(`${prefix}[file]`, license.file)
    }
  } else {
    if (license.file instanceof File) {
      if (license.name) formDataPayload.append(`${prefix}[title]`, license.name)
      if (license.description) formDataPayload.append(`${prefix}[notes]`, license.description)
      if (license.issueDate) {
        formDataPayload.append(`${prefix}[issue_date]`, dayjs(license.issueDate).format("YYYY-MM-DD"))
      }
      if (license.expiryDate) {
        formDataPayload.append(`${prefix}[expiry_date]`, dayjs(license.expiryDate).format("YYYY-MM-DD"))
      }
      formDataPayload.append(`${prefix}[file]`, license.file)
    }
  }
}

export const appendLicenses = (licenses, formDataPayload) => {
  licenses.forEach((license, index) => {
    appendLicense(license, index, formDataPayload)
  })
}

const appendTripAssignment = (assignment, index, formDataPayload) => {
  const prefix = `profile[trip_assignments_attributes][${index}]`

  if (assignment.id) {
    formDataPayload.append(`${prefix}[id]`, assignment.id)
    if (assignment._destroy) {
      formDataPayload.append(`${prefix}[_destroy]`, "true")
    } else {
      formDataPayload.append(`${prefix}[trip_id]`, assignment.trip_id)
    }
  } else {
    if (!assignment._destroy) {
      formDataPayload.append(`${prefix}[trip_id]`, assignment.trip_id)
    }
  }
}

export const appendTripAssignments = (tripAssignments, formDataPayload) => {
  tripAssignments.forEach((assignment, index) => {
    appendTripAssignment(assignment, index, formDataPayload)
  })
}

export const appendAvailabilitiesToDelete = (availabilityIds, formDataPayload, startIndex = 0) => {
  let attributeIndex = startIndex

  availabilityIds?.forEach((id) => {
    const prefix = `profile[availabilities_attributes][${attributeIndex}]`
    formDataPayload.append(`${prefix}[id]`, id)
    formDataPayload.append(`${prefix}[_destroy]`, "true")
    attributeIndex++
  })

  return attributeIndex
}

export const appendAllYearAvailability = (
  formData,
  selectedDays,
  availabilityIds,
  initialAvailabilityType,
  editMode,
  formDataPayload,
  startIndex = 0
) => {
  const timeBlocksData = {}
  const WEEKDAYS = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"]
  WEEKDAYS.forEach((day) => {
    timeBlocksData[day] = selectedDays.includes(day)
  })

  const prefix = `profile[availabilities_attributes][${startIndex}]`

  if (editMode && initialAvailabilityType === AVAILABILITY_TYPES.ALL_YEAR && availabilityIds.length > 0) {
    formDataPayload.append(`${prefix}[id]`, availabilityIds[0])
  }

  formDataPayload.append(`${prefix}[all_year]`, "true")
  formDataPayload.append(`${prefix}[time_blocks]`, JSON.stringify(timeBlocksData))
}

const appendSeasonalAvailability = (season, initialAvailabilityType, formDataPayload, index) => {
  const hasRealServerId = isServerGeneratedId(season.id)
  const prefix = `profile[availabilities_attributes][${index}]`

  if (season._destroy && hasRealServerId) {
    formDataPayload.append(`${prefix}[id]`, season.id)
    formDataPayload.append(`${prefix}[_destroy]`, "true")
    return true
  }

  if (!season._destroy && season.from && season.to) {
    const timeBlocksData = {}
    const WEEKDAYS = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"]
    WEEKDAYS.forEach((day) => {
      timeBlocksData[day] = season.selectedDays.includes(day)
    })

    if (hasRealServerId && initialAvailabilityType === AVAILABILITY_TYPES.SEASONAL) {
      formDataPayload.append(`${prefix}[id]`, season.id)
    }
    formDataPayload.append(`${prefix}[all_year]`, "false")
    formDataPayload.append(`${prefix}[season_start]`, dayjs(season.from, "MM/DD/YYYY").format("YYYY-MM-DD"))
    formDataPayload.append(`${prefix}[season_end]`, dayjs(season.to, "MM/DD/YYYY").format("YYYY-MM-DD"))
    formDataPayload.append(`${prefix}[recurring_yearly]`, season.recurringYearly)
    formDataPayload.append(`${prefix}[time_blocks]`, JSON.stringify(timeBlocksData))

    return true
  }

  return false
}

export const appendSeasonalAvailabilities = (seasons, initialAvailabilityType, formDataPayload, startIndex = 0) => {
  let attributeIndex = startIndex

  seasons?.forEach((season) => {
    const processed = appendSeasonalAvailability(season, initialAvailabilityType, formDataPayload, attributeIndex)
    if (processed) {
      attributeIndex++
    }
  })

  return attributeIndex
}

export const buildStaffPayload = ({
  formData,
  userId,
  licenses,
  tripAssignments,
  selectedDays,
  seasons,
  availabilityIds,
  initialAvailabilityType,
  editMode
}) => {
  const formDataPayload = new FormData()

  appendProfileFields(formData, formDataPayload, userId)

  appendLicenses(licenses, formDataPayload)

  appendTripAssignments(tripAssignments, formDataPayload)

  let attributeIndex = 0

  if (editMode && initialAvailabilityType && initialAvailabilityType !== formData.availability && availabilityIds.length > 0) {
    attributeIndex = appendAvailabilitiesToDelete(availabilityIds, formDataPayload, attributeIndex)
  }

  if (formData.availability === AVAILABILITY_TYPES.ALL_YEAR) {
    appendAllYearAvailability(formData, selectedDays, availabilityIds, initialAvailabilityType, editMode, formDataPayload, attributeIndex)
  } else if (formData.availability === AVAILABILITY_TYPES.SEASONAL) {
    appendSeasonalAvailabilities(seasons, initialAvailabilityType, formDataPayload, attributeIndex)
  }

  return formDataPayload
}
